import React, { useMemo, useState } from 'react';

import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { SortableList } from '@go-client/components/UIkit/SortableList/SortableList';

import './VenueRundownEditor.scss';
import { BubbleAlert, BubbleButton, BubbleInput } from '@go-client/components/BubbleUI';
import { mapBy } from '@go-shared/lib/functional';
import { FeedArea } from '@go-shared/types/feedTemplate';
import Fuse from 'fuse.js';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';

interface VenueRundownEditorProps {
  feedArea: FeedArea;
  venueRundownId: string;
  selectedVenues: VenueTemplate[];
  venueTemplates: VenueTemplate[];
  tab: 'venues' | 'order';
  onSetSelectedVenues: (venueTemplates: VenueTemplate[]) => void;
  onVenueRundownUpdated: (
    feedArea: FeedArea,
    selectedVenueRundownId: string,
    selectedVenueTemplates: VenueTemplate[]
  ) => void;
}

export const VenueRundownEditor: React.FC<VenueRundownEditorProps> = ({
  feedArea,
  venueRundownId,
  selectedVenues,
  venueTemplates,
  tab,
  onSetSelectedVenues,
  onVenueRundownUpdated,
}) => {
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchvied] = useState(false);
  const [filteredVenueTemplates, setFilteredVenueTemplates] =
    useState<VenueTemplate[]>(venueTemplates);

  // this will refresh the fuse database if the users have changed
  const fuse = useMemo(() => {
    return new Fuse(venueTemplates, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [venueTemplates]);

  useMemo(() => {
    const result = (search ? fuse.search(search).map(s => s.item) : venueTemplates).filter(vt =>
      !showArchived ? !vt.archived : true
    );
    setFilteredVenueTemplates(result);
  }, [fuse, search, showArchived]);

  const onVenueSelected = (checked: boolean, vt: VenueTemplate) => {
    const newSelectedVenues = [...selectedVenues];

    if (checked) {
      newSelectedVenues.push(vt);
    } else {
      const index = newSelectedVenues.findIndex(ct => ct._id === vt._id);
      newSelectedVenues.splice(index, 1);
    }

    onSetSelectedVenues(newSelectedVenues);
  };

  const reorganizeVenues = (newOrderedVenues: VenueTemplate[]) => {
    onSetSelectedVenues(newOrderedVenues);
  };

  const vtMap = mapBy(selectedVenues, '_id');

  return (
    <>
      <div className="bg-eggshell">
        {tab === 'venues' && (
          <div>
            <BubbleInput
              type="text"
              block
              appearance="bare"
              placeholder="Filter by name..."
              inputStyle={{ height: '49px' }}
              inputClassName="pl-5"
              value={search}
              onValue={setSearch}
              className="border-b"
            />
            <div className="p-4 grid gap-4 grid-cols-2 border-b">
              <div className="flex items-center">
                <BubbleInput
                  type="checkbox"
                  className="mr-2"
                  inputClassName="bg-white"
                  checked={showArchived}
                  onValue={setShowArchvied}
                  id="archived-checkbox"
                />
                <label htmlFor="archived-checkbox">Show Archived Venues</label>
              </div>
            </div>
            {filteredVenueTemplates.length > 0 && (
              <div className="p-4 grid gap-4 grid-cols-2">
                {filteredVenueTemplates.map(vt => (
                  <div key={vt._id} className="flex items-center">
                    <BubbleInput
                      type="checkbox"
                      className="mr-2"
                      inputClassName="bg-white"
                      checked={!!vtMap[vt._id]}
                      onValue={checked => onVenueSelected(checked, vt)}
                      id={`vt-${vt._id}`}
                    />
                    <label htmlFor={`vt-${vt._id}`}>{vt.name}</label>
                  </div>
                ))}
              </div>
            )}
            {filteredVenueTemplates.length === 0 && (
              <BubbleAlert variant="banana" className="rounded-0-i">
                No venues found
              </BubbleAlert>
            )}
          </div>
        )}
        {tab === 'order' && (
          <div className="p-4">{displaySelectedVenues(selectedVenues, reorganizeVenues)}</div>
        )}
      </div>
      <div className="p-4 sticky bottom-0 bg-white border-t">
        <BubbleButton
          variant="navy"
          icon="rotate"
          onClick={() =>
            onVenueRundownUpdated && onVenueRundownUpdated(feedArea, venueRundownId, selectedVenues)
          }
        >
          Sync Changes to Feed
        </BubbleButton>
      </div>
    </>
  );
};

const displaySelectedVenues = (
  selectedVenues: VenueTemplate[],
  onVenuesReorganized: (newVenues: VenueTemplate[]) => void
) => {
  return (
    <div className="grid grid-cols-1 gap-2">
      {selectedVenues.length < 1 && (
        <BubbleAlert variant="banana" className="w-full">
          There are no venues to sort
        </BubbleAlert>
      )}
      <SortableList
        items={selectedVenues.map(sv => {
          return {
            content: `${sv.name}`,
            id: sv._id,
            className: 'py-3 px-4 bg-white rounded-lg border shadow',
          };
        })}
        onListChanged={newItems => {
          onVenuesReorganized(
            newItems.map<VenueTemplate>(item => {
              return selectedVenues.find(vn => vn._id === item.id.toString())!;
            })
          );
        }}
      />
    </div>
  );
};
